import React, { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useFindOne, useAction } from '@gadgetinc/react';
import {
  Box,
  Container,
  Grid,
  Heading,
  Button,
  VStack,
  HStack,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  FormControl,
  FormLabel,
  Input,
  Checkbox,
  Textarea,
  useBreakpointValue,
  Divider,
  Badge,
} from '@chakra-ui/react';
import { api } from '../api';
import ScenarioDisplay from '../components/ScenarioDisplay';
import { buildScenarioDisplayProps } from '../lib/scenarioDisplayConfig';

export default function EvaluationView() {
  const { evaluationId } = useParams();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const evaluationType = searchParams.get('type');
  const [isEditing, setIsEditing] = useState(false);
  
  // Determine which API to use based on evaluation type
  const getEvalApi = () => {
    switch(evaluationType) {
      case 'ph4456':
        return api.ph4456Evaluation;
      case 'nremt_medical':
        return api.nremtMedicalEvaluation;
      case 'nremt_trauma':
        return api.nremtTraumaEvaluation;
      default:
        return null;
    }
  };
  
  const evalApi = getEvalApi();
  
  // Build selection based on evaluation type
  const getSelection = () => {
    const baseSelection = {
      id: true,
      createdAt: true,
      updatedAt: true,
      status: true,
      overallScore: true,
      comments: true,
      evaluationDate: true,
      completionTime: true,
      evaluationMetadata: true,
      simulationSession: {
        id: true,
        scenario: {
          id: true,
          title: true,
          description: { markdown: true },
          ageBracket: true,
          difficulty: true,
        },
      },
      student: {
        id: true,
        firstName: true,
        lastName: true,
        email: true,
      },
      instructor: {
        id: true,
        firstName: true,
        lastName: true,
        email: true,
      },
      cohort: {
        id: true,
        name: true,
        displayName: true,
      },
    };

    if (evaluationType === 'ph4456') {
      return {
        ...baseSelection,
        sceneSize: true,
        sceneHazards: true,
        numberOfPatients: true,
        mechanismOfInjury: true,
        bsiPrecautions: true,
        chiefComplaint: true,
        generalImpression: true,
        levelOfConsciousness: true,
        airwayPatency: true,
        breathingAdequacy: true,
        circulationAssessment: true,
        priorityDecision: true,
        vitalSigns: true,
        sample: true,
        opqrst: true,
        pertinentPositives: true,
        pertinentNegatives: true,
        historyPresent: true,
        physicalExam: true,
        diagnosticTools: true,
        needForResources: true,
        interventionRepeated: true,
        vitalSignsTrend: true,
        criticalFailures: true,
        formVersion: true,
      };
    } else if (evaluationType === 'nremt_medical') {
      return {
        ...baseSelection,
        sceneIsSafe: true,
        mechanismOfInjuryIllness: true,
        numberOfPatients: true,
        determinesResponsiveness: true,
        assessesAirwayBreathing: true,
        assessesPulse: true,
        assessesSkinColor: true,
        assessControlsMajorBleeding: true,
        identifiesPriorityPatients: true,
        verbalizesGeneralImpression: true,
        avpuAssessed: true,
        pulseAssessed: true,
        bloodPressureAssessed: true,
        respiratoryRateQualityAssessed: true,
        allergies: true,
        currentMedications: true,
        pastPertinentHistory: true,
        historyOfPresentIllness: true,
        clarifyingQuestions: true,
        criticalFailures: true,
        formVersion: true,
      };
    } else if (evaluationType === 'nremt_trauma') {
      return {
        ...baseSelection,
        sceneIsSafe: true,
        determinesMechanismInjury: true,
        determinesNumberPatients: true,
        determinesResponsiveness: true,
        assessesAirway: true,
        assessesBreathing: true,
        assessesCirculation: true,
        assessesControlsMajorBleeding: true,
        assessesSkinCondition: true,
        identifiesPriority: true,
        verbalizesGeneralImpression: true,
        inspectsHead: true,
        inspectsNeck: true,
        inspectsChest: true,
        inspectsAbdomen: true,
        inspectsUpperExtremities: true,
        inspectsLowerExtremities: true,
        assessesPosteriorThorax: true,
        criticalFailures: true,
        formVersion: true,
      };
    }

    return baseSelection;
  };
  
  const [{ data: evaluation, fetching, error }] = useFindOne(
    evalApi, 
    evaluationId, 
    { select: getSelection() }
  );
  
  const [{ fetching: updating }, updateEvaluation] = useAction(evalApi?.update);
  
  // Responsive layout
  const isMobile = useBreakpointValue({ base: true, lg: false });
  
  if (!evalApi || !evaluationType) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error">
          <AlertIcon />
          Invalid evaluation type. Please provide a valid type parameter (ph4456, nremt_medical, or nremt_trauma).
        </Alert>
      </Container>
    );
  }
  
  if (fetching) {
    return (
      <Container maxW="container.xl" py={8}>
        <VStack spacing={4}>
          <Spinner size="xl" />
          <Text>Loading evaluation...</Text>
        </VStack>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error">
          <AlertIcon />
          Error loading evaluation: {error.message}
        </Alert>
      </Container>
    );
  }
  
  if (!evaluation) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="warning">
          <AlertIcon />
          Evaluation not found.
        </Alert>
      </Container>
    );
  }
  
  const handleSave = async (formData) => {
    try {
      await updateEvaluation({
        id: evaluationId,
        ...formData,
      });
      
      toast({
        title: 'Evaluation updated',
        status: 'success',
        duration: 3000,
      });
      
      setIsEditing(false);
    } catch (err) {
      toast({
        title: 'Error updating evaluation',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    }
  };
  
  const handleCancel = () => {
    setIsEditing(false);
  };
  
  const scenario = evaluation.simulationSession?.scenario;
  const student = evaluation.student;
  const instructor = evaluation.instructor;
  const cohort = evaluation.cohort;
  
  const getEvaluationTypeName = () => {
    switch(evaluationType) {
      case 'ph4456':
        return 'PH4456 Patient Assessment';
      case 'nremt_medical':
        return 'NREMT Medical Patient Assessment';
      case 'nremt_trauma':
        return 'NREMT Trauma Patient Assessment';
      default:
        return 'Evaluation';
    }
  };
  
  const getStatusColor = (status) => {
    switch(status) {
      case 'completed':
        return 'green';
      case 'reviewed':
        return 'blue';
      case 'draft':
        return 'gray';
      default:
        return 'gray';
    }
  };
  
  return (
    <Container maxW="container.xl" py={8}>
      {/* Header */}
      <VStack align="stretch" spacing={4} mb={6}>
        <HStack justify="space-between" wrap="wrap">
          <VStack align="start" spacing={1}>
            <Heading size="lg">{scenario?.title || 'Evaluation'}</Heading>
            <Text fontSize="md" color="gray.600">{getEvaluationTypeName()}</Text>
          </VStack>
          <Button
            colorScheme="blue"
            onClick={() => setIsEditing(!isEditing)}
            isDisabled={updating}
          >
            {isEditing ? 'Cancel' : 'Edit'}
          </Button>
        </HStack>
        
        <Divider />
        
        <HStack spacing={6} wrap="wrap" fontSize="sm">
          <VStack align="start" spacing={0}>
            <Text color="gray.600">Student</Text>
            <Text fontWeight="medium">
              {student?.firstName} {student?.lastName}
            </Text>
          </VStack>
          
          <VStack align="start" spacing={0}>
            <Text color="gray.600">Instructor</Text>
            <Text fontWeight="medium">
              {instructor?.firstName} {instructor?.lastName}
            </Text>
          </VStack>
          
          {cohort && (
            <VStack align="start" spacing={0}>
              <Text color="gray.600">Cohort</Text>
              <Text fontWeight="medium">{cohort.displayName || cohort.name}</Text>
            </VStack>
          )}
          
          <VStack align="start" spacing={0}>
            <Text color="gray.600">Status</Text>
            <Badge colorScheme={getStatusColor(evaluation.status)}>
              {evaluation.status || 'Unknown'}
            </Badge>
          </VStack>
          
          {evaluation.evaluationDate && (
            <VStack align="start" spacing={0}>
              <Text color="gray.600">Date</Text>
              <Text fontWeight="medium">
                {new Date(evaluation.evaluationDate).toLocaleDateString()}
              </Text>
            </VStack>
          )}
          
          {evaluation.completionTime && (
            <VStack align="start" spacing={0}>
              <Text color="gray.600">Duration</Text>
              <Text fontWeight="medium">{evaluation.completionTime} min</Text>
            </VStack>
          )}
          
          {evaluation.overallScore !== null && evaluation.overallScore !== undefined && (
            <VStack align="start" spacing={0}>
              <Text color="gray.600">Score</Text>
              <Text fontWeight="medium" fontSize="lg" color="blue.600">
                {evaluation.overallScore}
              </Text>
            </VStack>
          )}
        </HStack>
      </VStack>
      
      {/* Content */}
      {isMobile ? (
        <Tabs colorScheme="blue">
          <TabList>
            <Tab>Scenario</Tab>
            <Tab>Evaluation</Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0}>
              {scenario && (
                <ScenarioDisplay
                  {...buildScenarioDisplayProps({
                    scenarioOutput: scenario,
                    allowPrintControls: true,
                  })}
                />
              )}
            </TabPanel>
            <TabPanel px={0}>
              <EvaluationForm
                evaluation={evaluation}
                evaluationType={evaluationType}
                isLocked={!isEditing}
                isUpdating={updating}
                onSave={handleSave}
                onCancel={handleCancel}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      ) : (
        <Grid templateColumns="1fr 1fr" gap={6}>
          <Box overflowY="auto" maxH="calc(100vh - 300px)">
            {scenario && (
              <ScenarioDisplay
                {...buildScenarioDisplayProps({
                  scenarioOutput: scenario,
                  allowPrintControls: true,
                })}
              />
            )}
          </Box>
          <Box overflowY="auto" maxH="calc(100vh - 300px)">
            <EvaluationForm
              evaluation={evaluation}
              evaluationType={evaluationType}
              isLocked={!isEditing}
              isUpdating={updating}
              onSave={handleSave}
              onCancel={handleCancel}
            />
          </Box>
        </Grid>
      )}
    </Container>
  );
}

// Evaluation Form Component
function EvaluationForm({ evaluation, evaluationType, isLocked, isUpdating, onSave, onCancel }) {
  const [formData, setFormData] = useState(evaluation);

  useEffect(() => {
    setFormData(evaluation);
  }, [evaluation]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const ph4456CheckboxFields = [
    { key: "sceneSize", label: "Takes or verbalizes BSI precautions" },
    { key: "sceneHazards", label: "Determines the scene is safe" },
    { key: "mechanismOfInjury", label: "Determines mechanism of injury / nature of illness" },
    { key: "numberOfPatients", label: "Determines the number of patients" },
    { key: "needForResources", label: "Requests additional resources as needed" },
    { key: "generalImpression", label: "Forms a general impression" },
    { key: "levelOfConsciousness", label: "Assesses level of consciousness" },
    { key: "chiefComplaint", label: "Identifies chief complaint / apparent life threats" },
    { key: "airwayPatency", label: "Assesses airway patency" },
    { key: "breathingAdequacy", label: "Assesses breathing adequacy" },
    { key: "circulationAssessment", label: "Assesses circulation" },
    { key: "priorityDecision", label: "Makes transport priority decision" },
    { key: "historyPresent", label: "Obtains relevant history" },
    { key: "opqrst", label: "Performs OPQRST history" },
    { key: "sample", label: "Performs SAMPLE history" },
    { key: "pertinentNegatives", label: "Identifies pertinent negatives" },
    { key: "pertinentPositives", label: "Identifies pertinent positives" },
    { key: "vitalSigns", label: "Obtains vital signs" },
    { key: "physicalExam", label: "Performs focused physical exam" },
    { key: "diagnosticTools", label: "Uses appropriate diagnostic tools" },
    { key: "interventionRepeated", label: "Performs / repeats indicated interventions" },
    { key: "vitalSignsTrend", label: "Trends or reassesses vital signs" },
  ];

  if (evaluationType === 'ph4456') {
    return (
      <Box as="form" onSubmit={handleSubmit}>
        <VStack align="stretch" spacing={4}>
          <Heading size="md" mb={2}>PH4456 Evaluation Form</Heading>

          <FormControl>
            <FormLabel fontWeight="bold">Overall Score</FormLabel>
            <Input
              type="number"
              value={formData.overallScore ?? ""}
              onChange={(e) => handleChange("overallScore", e.target.value === "" ? null : parseInt(e.target.value, 10))}
              isDisabled={isLocked}
              max={100}
              min={0}
            />
          </FormControl>

          <Heading size="sm" mt={2}>Checklist</Heading>

          <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={3}>
            {ph4456CheckboxFields.map((field) => (
              <FormControl key={field.key}>
                <Checkbox
                  isChecked={!!formData?.[field.key]}
                  onChange={(e) => handleChange(field.key, e.target.checked)}
                  isDisabled={isLocked}
                >
                  {field.label}
                </Checkbox>
              </FormControl>
            ))}
          </Grid>

          <FormControl>
            <FormLabel fontWeight="bold">Critical Failures</FormLabel>
            <Textarea
              value={Array.isArray(formData.criticalFailures) ? formData.criticalFailures.join("\n") : (formData.criticalFailures || "")}
              onChange={(e) => handleChange("criticalFailures", e.target.value)}
              isDisabled={isLocked}
              rows={4}
              placeholder="Enter critical failures, one per line"
            />
          </FormControl>

          <FormControl>
            <FormLabel fontWeight="bold">Comments</FormLabel>
            <Textarea
              value={formData.comments || ""}
              onChange={(e) => handleChange("comments", e.target.value)}
              isDisabled={isLocked}
              rows={6}
              placeholder="Evaluation comments"
            />
          </FormControl>

          <HStack justify="flex-end">
            <Button variant="outline" onClick={onCancel} isDisabled={isUpdating}>
              Cancel
            </Button>
            <Button type="submit" colorScheme="blue" isLoading={isUpdating} isDisabled={isLocked}>
              Save Changes
            </Button>
          </HStack>
        </VStack>
      </Box>
    );
  }

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <VStack align="stretch" spacing={4}>
        <Alert status="info">
          <AlertIcon />
          Detailed inline editing is currently only enabled for PH4456 evaluations. Other evaluation types can still be reviewed here.
        </Alert>

        <FormControl>
          <FormLabel fontWeight="bold">Comments</FormLabel>
          <Textarea
            value={formData.comments || ""}
            onChange={(e) => handleChange("comments", e.target.value)}
            isDisabled={isLocked}
            rows={6}
            placeholder="Evaluation comments"
          />
        </FormControl>

        <HStack justify="flex-end">
          <Button variant="outline" onClick={onCancel} isDisabled={isUpdating}>
            Cancel
          </Button>
          <Button type="submit" colorScheme="blue" isLoading={isUpdating} isDisabled={isLocked}>
            Save Changes
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
}
